import type { UseFormReturnType } from '@mantine/form'
import type { FormPathValue } from '@mantine/form/lib/paths.types'
import { uniqBy } from 'lodash-es'
import { useRef } from 'react'
import {
  Button,
  Disclosure,
  DisclosurePanel,
  Heading,
} from 'react-aria-components'
import { useTranslation } from 'react-i18next'
import type { Task } from 'src/services/apollo/generated/graphql'
import { ArtworkVisibilityType } from 'src/services/apollo/generated/graphql'
import { TagRecommendation } from '../Tag/TagRecommendation'
import { TagSelect } from '../Tag/TagSelect'
import { type TagItem } from '../Tag/utils'
import { getTagCategory, getTagName, TagCategory } from '../Tag/utils'
import { UICheckbox } from '../ui/Checkbox'
import { UIFormLabel, UIFormTextField } from '../ui/Form'
import { UIInput, UITextArea } from '../ui/TextField'
import type { DetailParamsEditFormValues } from './DetailParamsEditFormItems'
import { DetailParamsEditFormItems } from './DetailParamsEditFormItems'
import { TitleTagSuggestion } from './TitleTagSuggestion'

type ArtworkInfoFormFields = {
  prompts?: boolean
  tags?: boolean
  description?: boolean
  parameters?: boolean
  hidePrompts?: boolean
  setPrivate?: boolean
  autoPublish?: boolean
}

export type ArtworkInfoFormProps<TFormValue extends ArtworkInfoFormValue> = {
  form: UseFormReturnType<TFormValue>
  fields?: ArtworkInfoFormFields
  mediaId?: string
  taskIsVideoTask?: boolean
  isEditingArtwork?: boolean
}

export type ArtworkInfoFormValue = {
  title: string
  prompts: string
  tags: TagItem[]
  tackIds?: string[]
  hidePrompts: boolean
  visibility: ArtworkVisibilityType
  autoPublish: boolean
  challenge?: number
  description: string
  extra: {
    externalParameters?: DetailParamsEditFormValues
  }
}

export const mapTaskToArtworkInfoFormValue = (
  task: Task,
  extraInitials?: Partial<ArtworkInfoFormValue>,
): ArtworkInfoFormValue => {
  const artworks = task.artworks ?? []
  const artwork = artworks.length > 0 ? artworks[0] : undefined

  return {
    title: extraInitials?.title ?? artwork?.title ?? '',
    prompts:
      extraInitials?.prompts ??
      artwork?.prompts ??
      task.parameters?.prompts ??
      '',
    tags: extraInitials?.tags ?? artwork?.tags ?? [],
    hidePrompts: extraInitials?.hidePrompts ?? artwork?.hidePrompts ?? false,
    visibility:
      extraInitials?.visibility ??
      artwork?.visibility ??
      ArtworkVisibilityType.Public,
    autoPublish: extraInitials?.autoPublish ?? false,
    challenge: extraInitials?.challenge ?? undefined,
    description: extraInitials?.description ?? '',
    extra: extraInitials?.extra ?? {},
    tackIds: extraInitials?.tackIds ?? [],
  }
}

export const mergeArtworkInfoFormValue = (
  first?: Partial<ArtworkInfoFormValue>,
  second?: Partial<ArtworkInfoFormValue>,
): ArtworkInfoFormValue => {
  return {
    title: first?.title ?? second?.title ?? '',
    prompts: first?.prompts ?? second?.prompts ?? '',
    tags: [...(first?.tags ?? []), ...(second?.tags ?? [])],
    hidePrompts: first?.hidePrompts ?? second?.hidePrompts ?? false,
    visibility:
      first?.visibility ?? second?.visibility ?? ArtworkVisibilityType.Public,
    autoPublish: first?.autoPublish ?? second?.autoPublish ?? false,
    challenge: first?.challenge ?? second?.challenge,
    description: first?.description ?? second?.description ?? '',
    extra: {
      externalParameters:
        first?.extra?.externalParameters ?? second?.extra?.externalParameters,
    },
    tackIds: [...(first?.tackIds ?? []), ...(second?.tackIds ?? [])],
  }
}

export const mergeArtworkInfoFormFields = (
  first?: ArtworkInfoFormFields,
  second?: ArtworkInfoFormFields,
): ArtworkInfoFormFields => {
  return {
    prompts: first?.prompts ?? second?.prompts,
    tags: first?.tags ?? second?.tags,
    description: first?.description ?? second?.description,
    parameters: first?.parameters ?? second?.parameters,
    hidePrompts: first?.hidePrompts ?? second?.hidePrompts,
    setPrivate: first?.setPrivate ?? second?.setPrivate,
    autoPublish: first?.autoPublish ?? second?.autoPublish,
  }
}

export const ArtworkInfoForm = <TFormValue extends ArtworkInfoFormValue>(
  props: ArtworkInfoFormProps<TFormValue>,
) => {
  const {
    form,
    fields: {
      prompts: showPrompts = true,
      tags: showTags = true,
      description: showDescription = false,
      parameters: showParameters = false,
      hidePrompts: showHidePrompts = true,
      setPrivate: showSetPrivate = true,
      autoPublish: showAutoPublish = false,
    } = {},
    mediaId,
    taskIsVideoTask,
    isEditingArtwork,
  } = props

  const { t } = useTranslation(['artwork'])
  const recommendationInitialized = useRef(false)

  const onTagRecommendationLoaded = (tags: TagItem[]) => {
    if (recommendationInitialized.current) return
    recommendationInitialized.current = true

    if (isEditingArtwork) return

    const oldTags = form.getValues().tags
    const newTags = uniqBy([...tags, ...oldTags], getTagName)
    form.setFieldValue('tags', newTags as FormPathValue<TFormValue, 'tags'>)
  }

  const onTagRemove = (tag: TagItem) => {
    const tagName = getTagName(tag)
    const oldTags = form.getValues().tags
    const newTags = oldTags.filter(tag => getTagName(tag) !== tagName)
    form.setFieldValue('tags', newTags as FormPathValue<TFormValue, 'tags'>)

    // Reset challenge when a contest tag is removed
    if (getTagCategory(tag) === TagCategory.Contest) {
      form.setFieldValue(
        'challenge',
        undefined as FormPathValue<TFormValue, 'challenge'>,
      )
    }
  }

  const onTagSelected = (tag: TagItem) => {
    const oldTags = form.getValues().tags
    if (oldTags.some(t => getTagName(t) === getTagName(tag))) {
      return
    }
    form.setFieldValue('tags', [...oldTags, tag] as FormPathValue<
      TFormValue,
      'tags'
    >)
  }

  const onChallengeChange = (challenge?: number) => {
    form.setFieldValue(
      'challenge',
      challenge as FormPathValue<TFormValue, 'challenge'>,
    )
  }

  return (
    <>
      <UIFormTextField input={form.getInputProps('title')} layout="vertical">
        {input => (
          <>
            <UIFormLabel>{t('artwork:title')}</UIFormLabel>
            <div className="flex items-center gap-2">
              <UIInput
                {...input}
                variant="rounded-normal"
                placeholder={t('artwork:title-placeholder')}
                className="flex-1"
              />
              {mediaId && (
                <TitleTagSuggestion
                  mediaId={mediaId}
                  isVideoTask={taskIsVideoTask}
                  onSelectTag={name => {
                    const current = form.getValues().title.trim()
                    const next = current ? `${current} ${name}` : name
                    form.setFieldValue(
                      'title',
                      next as FormPathValue<TFormValue, 'title'>,
                    )
                  }}
                />
              )}
            </div>
          </>
        )}
      </UIFormTextField>
      {showDescription && (
        <UIFormTextField
          input={form.getInputProps('description')}
          layout="vertical"
        >
          {input => (
            <>
              <UIFormLabel>{t('artwork:description')}</UIFormLabel>
              <UITextArea
                variant="rounded-normal"
                value={input.value}
                defaultValue={input.defaultValue}
                onChange={e =>
                  form.setFieldValue(
                    'description',
                    e.target.value as FormPathValue<TFormValue, 'description'>,
                  )
                }
                rows={3}
              />
            </>
          )}
        </UIFormTextField>
      )}
      {showPrompts && (
        <UIFormTextField
          input={form.getInputProps('prompts')}
          layout="vertical"
        >
          {input => (
            <>
              <UIFormLabel>{t('artwork:prompts.prompts')}</UIFormLabel>
              <UITextArea
                variant="rounded-normal"
                value={input.value}
                defaultValue={input.defaultValue}
                onChange={e =>
                  form.setFieldValue(
                    'prompts',
                    e.target.value as FormPathValue<TFormValue, 'prompts'>,
                  )
                }
                placeholder={t('artwork:prompts-placeholder')}
                rows={3}
              />
            </>
          )}
        </UIFormTextField>
      )}

      {showParameters && (
        <Disclosure>
          <Heading>
            <Button slot="trigger">{t('artwork:detail-parameters')}</Button>
          </Heading>
          <DisclosurePanel>
            <div className="flex flex-col gap-4 pb-4">
              <DetailParamsEditFormItems form={form} />
            </div>
          </DisclosurePanel>
        </Disclosure>
      )}
      {showTags && (
        <>
          <TagSelect
            value={form.getValues().tags}
            label={t('artwork:tags')}
            onTagSelected={onTagSelected}
            onTagRemove={onTagRemove}
          />

          <TagRecommendation
            selectedTags={form.getValues().tags}
            onTagSelected={onTagSelected}
            onTagRemove={onTagRemove}
            onTagRecommendationLoaded={onTagRecommendationLoaded}
            challenge={form.getValues().challenge}
            onChallengeChange={onChallengeChange}
            mediaId={mediaId}
            isVideoTask={taskIsVideoTask ?? false}
          />
        </>
      )}
      {showHidePrompts && (
        <UICheckbox
          mantineInput={form.getInputProps('hidePrompts')}
          description={t('artwork:generation.hide-prompts-description')}
        >
          {t('artwork:generation.hide-prompts')}
        </UICheckbox>
      )}
      {showSetPrivate && (
        <UICheckbox
          mantineInput={{
            value:
              form.getValues().visibility === ArtworkVisibilityType.Private,
            onChange: (checked: boolean) =>
              form.setFieldValue(
                'visibility',
                (checked
                  ? ArtworkVisibilityType.Private
                  : ArtworkVisibilityType.Public) as FormPathValue<
                  TFormValue,
                  'visibility'
                >,
              ),
          }}
          description={t('artwork:generation.is-private-description')}
        >
          {t('artwork:generation.is-private')}
        </UICheckbox>
      )}
      {showAutoPublish && (
        <UICheckbox
          mantineInput={form.getInputProps('autoPublish')}
          description={t('artwork:auto-publish-description')}
        >
          {t('artwork:generation.auto-publish')}
        </UICheckbox>
      )}
    </>
  )
}
